/**
 * A rotina agendada, montada por escolhas: o que a consulta recebe e o que a
 * ação faz com cada linha.
 *
 * - A data que a consulta recebe se escolhe ("Amanhã"); o outro valor é fixo.
 * - A etapa e a pessoa da ação se escolhem da lista do CRM.
 * - O contato sai de uma coluna da linha, dizendo se é telefone, CPF ou CNPJ.
 * - O texto aceita `{coluna}`: "Consulta de {paciente}".
 *
 * Tudo vira a expressão que o serviço avalia. "Escrever a expressão" fica para
 * o que as escolhas não cobrem.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DIAS_RELATIVOS,
  diasDaExpressao,
  expressaoDaData,
  nomeDosDias,
} from '@/shared/lib/datas-em-expressao';
import { expressaoDoValor, textoFixo, valorFixo } from '@/shared/lib/valor-fixo';
import { Botao, Campo, CampoSelecao } from '@/shared/ui';
import {
  LINHA_DA_ROTINA,
  caminhoDaColuna,
  camposDaOportunidadeDaExpressao,
  contatoDaExpressao,
  expressaoDoContato,
  expressaoDoTexto,
  expressaoDaOportunidade,
  expressaoDosCamposDaOportunidade,
  oportunidadeDaExpressao,
  textoDaExpressao,
  type ComoAcharAOportunidade,
  type ComoAcharOContato,
  type ParametroDaAcao,
} from '../lib/modelos';

/** Os valores que a consulta recebe, na ordem que ela declara. */
export function ParametrosDaRotina({
  parametros,
  valores,
  problemas,
  aoMudar,
}: {
  parametros: readonly { nome: string; tipo: string }[];
  valores: readonly string[];
  problemas: Readonly<Record<string, string | undefined>>;
  aoMudar: (valores: string[]) => void;
}): ReactNode {
  if (parametros.length === 0) return null;
  const mudar = (i: number, valor: string): void =>
    aoMudar(parametros.map((_, j) => (j === i ? valor : (valores[j] ?? ''))));
  return (
    <fieldset className="configurar-modelo__grupo">
      <legend>O que a consulta recebe</legend>
      {parametros.map((p, i) => {
        const erro = problemas[`operacao.${i}`];
        if (p.tipo === 'data') {
          const dias = diasDaExpressao(valores[i] ?? '');
          return (
            <CampoSelecao
              key={p.nome}
              rotulo={p.nome}
              ajuda="O dia é contado quando a rotina roda, no horário de Brasília."
              value={dias === null ? '' : String(dias)}
              onChange={(e) =>
                mudar(i, e.target.value ? expressaoDaData(Number(e.target.value)) : '')
              }
              {...(erro ? { erro } : {})}
            >
              <option value="">Escolha o dia</option>
              {DIAS_RELATIVOS.map((d) => (
                <option key={d} value={String(d)}>
                  {nomeDosDias(d)}
                </option>
              ))}
            </CampoSelecao>
          );
        }
        return (
          <ValorFixo
            key={p.nome}
            rotulo={p.nome}
            tipo={p.tipo}
            valor={valores[i] ?? ''}
            aoMudar={(v) => mudar(i, v)}
            {...(erro ? { erro } : {})}
          />
        );
      })}
    </fieldset>
  );
}

/**
 * Um valor fixo, no tipo do parâmetro: o número sem aspas, o sim ou não como
 * escolha, o texto entre aspas. Mandar `"5"` a um parâmetro numérico faria o
 * banco comparar texto com número.
 */
function ValorFixo({
  rotulo,
  tipo,
  valor,
  aoMudar,
  erro,
}: {
  rotulo: string;
  tipo: string;
  valor: string;
  aoMudar: (expressao: string) => void;
  erro?: string;
}): ReactNode {
  const lido = valorFixo(valor);
  const [texto, definirTexto] = useState(() =>
    lido === undefined || lido === null ? '' : String(lido),
  );
  if (tipo === 'booleano') {
    return (
      <CampoSelecao
        rotulo={rotulo}
        value={lido === true ? 'sim' : lido === false ? 'nao' : ''}
        onChange={(e) =>
          aoMudar(e.target.value === '' ? '' : expressaoDoValor(e.target.value === 'sim'))
        }
        {...(erro ? { erro } : {})}
      >
        <option value="">Escolha</option>
        <option value="sim">Sim</option>
        <option value="nao">Não</option>
      </CampoSelecao>
    );
  }
  const numero = tipo === 'numero';
  const numeroInvalido =
    numero && texto.trim() !== '' && !Number.isFinite(Number(texto.replace(',', '.')));
  return (
    <Campo
      rotulo={rotulo}
      {...(numero ? { inputMode: 'decimal' as const } : {})}
      value={texto}
      onChange={(e) => {
        const bruto = e.target.value;
        definirTexto(bruto);
        const limpo = bruto.trim();
        if (!limpo) return aoMudar('');
        if (numero) {
          const n = Number(limpo.replace(',', '.'));
          return aoMudar(Number.isFinite(n) ? expressaoDoValor(n) : '');
        }
        aoMudar(expressaoDoValor(limpo));
      }}
      {...(numeroInvalido ? { erro: 'Use um número, como 10 ou 2,5.' } : erro ? { erro } : {})}
    />
  );
}

/** O que o evento traz para a ação, quando ela roda num evento e não numa linha. */
export type ReferenciaDoMenu = {
  readonly tipo: 'conversa' | 'oportunidade' | 'pessoa';
  readonly caminho: string;
  readonly titulo: string;
};

/** O valor da escolha "Ninguém" no seletor; na expressão, ela é `null`. */
const NINGUEM = '\u0000ninguem';

/** Um dado da ação, pelo tipo dele: escolher, apontar a coluna ou escrever com `{coluna}`. */
export function DadoDaAcao({
  acao,
  parametro: p,
  colunas,
  valor,
  aoMudar,
  erro,
  rotulo: rotuloDeFora,
  doEvento,
}: {
  acao: string;
  parametro: ParametroDaAcao;
  /**
   * Quando a ação roda num evento (a opção do menu), e não em cada linha de uma
   * consulta: o que o evento traz. Sem ele, os dados saem das colunas da linha.
   */
  doEvento?: readonly ReferenciaDoMenu[];
  /** O nome do campo, quando quem usa precisa de outro ("Opção 1: Título"). */
  rotulo?: string;
  /** As colunas que cada linha traz (as de saída da consulta). */
  colunas: readonly string[];
  valor: string;
  aoMudar: (expressao: string) => void;
  erro?: string;
}): ReactNode {
  const [escrevendo, definirEscrevendo] = useState(false);
  const rotulo = rotuloDeFora ?? (p.obrigatorio ? p.titulo : `${p.titulo} (opcional)`);
  const comErro = erro ? { erro } : {};
  const escrever = (
    <Botao variante="fantasma" miudo onClick={() => definirEscrevendo(true)}>
      Escrever a expressão
    </Botao>
  );

  if (escrevendo) {
    return (
      <div className="configurar-modelo__dado">
        <Campo
          rotulo={rotulo}
          ajuda={`Use ${LINHA_DA_ROTINA}.coluna para um valor da linha. Texto fixo vai entre aspas.`}
          spellCheck={false}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          {...comErro}
        />
        <Botao
          variante="fantasma"
          miudo
          onClick={() => {
            definirEscrevendo(false);
            aoMudar('');
          }}
        >
          Voltar às opções
        </Botao>
      </div>
    );
  }

  // Na opção do menu, a ação roda na conversa de quem escolheu: os dados vêm do evento.
  if (doEvento) {
    if (p.tipo === 'conversa' || p.tipo === 'oportunidade') {
      const daqui = doEvento.filter((r) => r.tipo === p.tipo);
      return (
        <div className="configurar-modelo__dado">
          <DoEventoDoMenu
            rotulo={rotulo}
            obrigatorio={p.obrigatorio}
            referencias={daqui}
            valor={valor}
            aoMudar={aoMudar}
            {...comErro}
          />
          {escrever}
        </div>
      );
    }
    if (p.tipo === 'texto' && p.valor_fixo !== false) {
      return (
        <div className="configurar-modelo__dado">
          <Campo
            rotulo={rotulo}
            value={textoFixo(valor) ?? ''}
            onChange={(e) => aoMudar(e.target.value.trim() ? expressaoDoValor(e.target.value) : '')}
            {...comErro}
          />
          {escrever}
        </div>
      );
    }
  }

  /*
   * O valor salvo (editar a rotina) que as escolhas não sabem mostrar fica à
   * vista como expressão: as escolhas vazias fariam parecer que não há nada, e
   * mexer nelas apagaria o que está salvo.
   */
  const salvoIlegivel =
    valor.trim() !== '' &&
    ((((p.opcoes && p.opcoes.length > 0) || p.opcao_nula) &&
      !(p.opcao_nula && valorFixo(valor) === null) &&
      !p.opcoes?.some((o) => o.valor === textoFixo(valor))) ||
      (p.tipo === 'conversa' &&
        colunas.length > 0 &&
        !colunas.some((c) => caminhoDaColuna(c) === valor.trim())) ||
      (p.tipo === 'contato' && colunas.length > 0 && !contatoDaExpressao(valor, colunas)) ||
      (p.tipo === 'campos' &&
        acao === 'criar_oportunidade' &&
        !camposDaOportunidadeDaExpressao(valor, colunas)) ||
      (p.tipo === 'texto' && textoDaExpressao(valor, colunas) === null));
  if (salvoIlegivel) {
    return (
      <div className="configurar-modelo__dado">
        <Campo
          rotulo={rotulo}
          ajuda="O valor salvo não cabe nas escolhas desta tela. Ele segue como está."
          spellCheck={false}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          {...comErro}
        />
        <Botao variante="fantasma" miudo onClick={() => aoMudar('')}>
          Escolher de novo
        </Botao>
      </div>
    );
  }

  if ((p.opcoes && p.opcoes.length > 0) || p.opcao_nula) {
    const fixo = valorFixo(valor);
    const escolhido = fixo === null && p.opcao_nula ? NINGUEM : (textoFixo(valor) ?? '');
    return (
      <div className="configurar-modelo__dado">
        <CampoSelecao
          rotulo={rotulo}
          value={escolhido}
          onChange={(e) =>
            aoMudar(
              e.target.value === NINGUEM
                ? expressaoDoValor(null)
                : e.target.value
                  ? expressaoDoValor(e.target.value)
                  : '',
            )
          }
          {...comErro}
        >
          <option value="">Escolha</option>
          {p.opcao_nula && <option value={NINGUEM}>{p.opcao_nula}</option>}
          {(p.opcoes ?? []).map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
        </CampoSelecao>
        {escrever}
      </div>
    );
  }

  // A oportunidade de cada linha: a coluna que traz o id dela, ou a referência.
  if (p.tipo === 'oportunidade' && colunas.length > 0) {
    return (
      <OportunidadePelaColuna
        rotulo={rotulo}
        colunas={colunas}
        valor={valor}
        aoMudar={aoMudar}
        rodape={escrever}
        {...comErro}
      />
    );
  }

  // A conversa de cada linha: a coluna que traz o id dela.
  if (p.tipo === 'conversa' && colunas.length > 0) {
    const coluna = colunas.find((c) => caminhoDaColuna(c) === valor.trim()) ?? '';
    return (
      <div className="configurar-modelo__dado">
        <CampoSelecao
          rotulo={rotulo}
          ajuda="A coluna da linha que traz o id da conversa."
          value={coluna}
          onChange={(e) => aoMudar(e.target.value ? caminhoDaColuna(e.target.value) : '')}
          {...comErro}
        >
          <option value="">Escolha a coluna</option>
          {colunas.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </CampoSelecao>
        {escrever}
      </div>
    );
  }

  if (p.tipo === 'contato' && colunas.length > 0) {
    return (
      <ContatoPelaColuna
        rotulo={rotulo}
        colunas={colunas}
        valor={valor}
        aoMudar={aoMudar}
        rodape={escrever}
        {...comErro}
      />
    );
  }

  if (p.tipo === 'campos' && acao === 'criar_oportunidade') {
    return (
      <CamposDaOportunidade
        colunas={colunas}
        valor={valor}
        aoMudar={aoMudar}
        rodape={escrever}
        {...comErro}
      />
    );
  }

  if (p.tipo === 'texto') {
    return (
      <TextoComColunas
        rotulo={rotulo}
        colunas={colunas}
        valor={valor}
        aoMudar={aoMudar}
        rodape={escrever}
        {...comErro}
      />
    );
  }

  return (
    <Campo
      rotulo={rotulo}
      ajuda={`Use ${LINHA_DA_ROTINA}.coluna para um valor da linha.`}
      spellCheck={false}
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      {...comErro}
    />
  );
}

function ajudaDasColunas(colunas: readonly string[]): string {
  return colunas.length > 0
    ? `Para um valor da linha, escreva a coluna entre chaves: ${colunas
        .slice(0, 4)
        .map((c) => `{${c}}`)
        .join(', ')}.`
    : 'A consulta não declarou as colunas de saída: o texto vai como está.';
}

function TextoComColunas({
  rotulo,
  colunas,
  valor,
  aoMudar,
  rodape,
  erro,
}: {
  rotulo: string;
  colunas: readonly string[];
  /** A expressão salva: o campo abre com o texto dela. */
  valor: string;
  aoMudar: (expressao: string) => void;
  rodape: ReactNode;
  erro?: string;
}): ReactNode {
  const [texto, definirTexto] = useState(() => textoDaExpressao(valor, colunas) ?? '');
  return (
    <div className="configurar-modelo__dado">
      <Campo
        rotulo={rotulo}
        ajuda={ajudaDasColunas(colunas)}
        value={texto}
        onChange={(e) => {
          definirTexto(e.target.value);
          aoMudar(e.target.value.trim() ? expressaoDoTexto(e.target.value.trim(), colunas) : '');
        }}
        {...(erro ? { erro } : {})}
      />
      {rodape}
    </div>
  );
}

const COMO: Record<ComoAcharOContato, string> = {
  telefone: 'Telefone',
  cpf: 'CPF',
  cnpj: 'CNPJ',
};

function ContatoPelaColuna({
  rotulo,
  colunas,
  valor,
  aoMudar,
  rodape,
  erro,
}: {
  rotulo: string;
  colunas: readonly string[];
  /** A expressão salva: a coluna e o jeito abrem escolhidos. */
  valor: string;
  aoMudar: (expressao: string) => void;
  rodape: ReactNode;
  erro?: string;
}): ReactNode {
  const salvo = contatoDaExpressao(valor, colunas);
  const [coluna, definirColuna] = useState(salvo?.coluna ?? '');
  const [como, definirComo] = useState<ComoAcharOContato>(salvo?.como ?? 'telefone');
  const mudar = (c: string, k: ComoAcharOContato): void => {
    definirColuna(c);
    definirComo(k);
    aoMudar(c ? expressaoDoContato(c, k) : '');
  };
  return (
    <fieldset className="configurar-modelo__grupo">
      <legend>{rotulo}</legend>
      <p className="configurar-modelo__ajuda">
        O contato é achado pelo telefone ou pelo documento; se não existir, nasce um novo.
      </p>
      <CampoSelecao
        rotulo="A coluna da linha"
        value={coluna}
        onChange={(e) => mudar(e.target.value, como)}
        {...(erro ? { erro } : {})}
      >
        <option value="">Escolha a coluna</option>
        {colunas.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </CampoSelecao>
      <CampoSelecao
        rotulo="O que a coluna traz"
        value={como}
        onChange={(e) => mudar(coluna, e.target.value as ComoAcharOContato)}
      >
        {(Object.keys(COMO) as ComoAcharOContato[]).map((k) => (
          <option key={k} value={k}>
            {COMO[k]}
          </option>
        ))}
      </CampoSelecao>
      {rodape}
    </fieldset>
  );
}

const COMO_A_OPORTUNIDADE: Record<ComoAcharAOportunidade, string> = {
  referencia: 'A referência usada no "Criar oportunidade"',
  id: 'O id da oportunidade no CRM',
};

function OportunidadePelaColuna({
  rotulo,
  colunas,
  valor,
  aoMudar,
  rodape,
  erro,
}: {
  rotulo: string;
  colunas: readonly string[];
  valor: string;
  aoMudar: (expressao: string) => void;
  rodape: ReactNode;
  erro?: string;
}): ReactNode {
  // O valor manda: a versão recarregada da automação mostra o que está nela. O
  // rascunho só guarda o "o que a coluna traz" escolhido antes da coluna.
  const [rascunho, definirRascunho] = useState<ComoAcharAOportunidade>('referencia');
  const lida = oportunidadeDaExpressao(valor, colunas);
  // Um valor salvo que não é de nenhuma coluna da lista (as colunas não
  // carregaram, ou a coluna mudou de nome na consulta) fica à vista como
  // expressão: as escolhas vazias fariam parecer que não há nada, e mexer
  // nelas apagaria o que está salvo.
  if (!lida && valor.trim()) {
    return (
      <div className="configurar-modelo__dado">
        <Campo
          rotulo={rotulo}
          ajuda="Nenhuma coluna da consulta bate com o valor salvo. Ele segue como está."
          spellCheck={false}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          {...(erro ? { erro } : {})}
        />
        <Botao variante="fantasma" miudo onClick={() => aoMudar('')}>
          Escolher a coluna de novo
        </Botao>
      </div>
    );
  }
  const coluna = lida?.coluna ?? '';
  const como = lida?.como ?? rascunho;
  const mudar = (c: string, k: ComoAcharAOportunidade): void => {
    definirRascunho(k);
    aoMudar(c ? expressaoDaOportunidade(c, k) : '');
  };
  return (
    <fieldset className="configurar-modelo__grupo">
      <legend>{rotulo}</legend>
      <p className="configurar-modelo__ajuda">
        Pela referência, a linha acha o card que outra automação criou com ela. Linha cuja
        referência ainda não tem card é pulada, sem erro.
      </p>
      <CampoSelecao
        rotulo="A coluna da linha"
        value={coluna}
        onChange={(e) => mudar(e.target.value, como)}
        {...(erro ? { erro } : {})}
      >
        <option value="">Escolha a coluna</option>
        {colunas.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </CampoSelecao>
      <CampoSelecao
        rotulo="O que a coluna traz"
        value={como}
        onChange={(e) => mudar(coluna, e.target.value as ComoAcharAOportunidade)}
      >
        {(Object.keys(COMO_A_OPORTUNIDADE) as ComoAcharAOportunidade[]).map((k) => (
          <option key={k} value={k}>
            {COMO_A_OPORTUNIDADE[k]}
          </option>
        ))}
      </CampoSelecao>
      {rodape}
    </fieldset>
  );
}

function CamposDaOportunidade({
  colunas,
  valor,
  aoMudar,
  rodape,
  erro,
}: {
  colunas: readonly string[];
  /** A expressão salva: os dois nomes abrem preenchidos. */
  valor: string;
  aoMudar: (expressao: string) => void;
  rodape: ReactNode;
  erro?: string;
}): ReactNode {
  const salvo = camposDaOportunidadeDaExpressao(valor, colunas);
  const [nome, definirNome] = useState(salvo?.nome ?? '');
  const [nomeDoContato, definirNomeDoContato] = useState(salvo?.nomeDoContato ?? '');
  const mudar = (n: string, c: string): void => {
    definirNome(n);
    definirNomeDoContato(c);
    aoMudar(expressaoDosCamposDaOportunidade(n, c, colunas));
  };
  return (
    <fieldset className="configurar-modelo__grupo">
      <legend>O card</legend>
      <p className="configurar-modelo__ajuda">{ajudaDasColunas(colunas)}</p>
      <Campo
        rotulo="Nome da oportunidade (opcional)"
        placeholder="Ex.: Consulta de {paciente}"
        value={nome}
        onChange={(e) => mudar(e.target.value, nomeDoContato)}
        {...(erro ? { erro } : {})}
      />
      <Campo
        rotulo="Nome do contato, se ele for novo (opcional)"
        placeholder="Ex.: {paciente}"
        value={nomeDoContato}
        onChange={(e) => mudar(nome, e.target.value)}
      />
      {rodape}
    </fieldset>
  );
}

/** A conversa (ou a oportunidade) do evento, pelo nome; a única já vem escolhida no obrigatório. */
function DoEventoDoMenu({
  rotulo,
  obrigatorio,
  referencias,
  valor,
  aoMudar,
  erro,
}: {
  rotulo: string;
  obrigatorio: boolean;
  referencias: readonly ReferenciaDoMenu[];
  valor: string;
  aoMudar: (expressao: string) => void;
  erro?: string;
}): ReactNode {
  const jaPreencheu = useRef(false);
  const unica = referencias.length === 1 ? referencias[0]!.caminho : null;
  useEffect(() => {
    if (jaPreencheu.current) return;
    jaPreencheu.current = true;
    if (obrigatorio && !valor.trim() && unica) aoMudar(unica);
  }, [obrigatorio, valor, unica, aoMudar]);
  return (
    <CampoSelecao
      rotulo={rotulo}
      value={referencias.find((r) => r.caminho === valor.trim())?.caminho ?? ''}
      onChange={(e) => aoMudar(e.target.value)}
      {...(erro ? { erro } : {})}
    >
      <option value="">Escolha</option>
      {referencias.map((r) => (
        <option key={r.caminho} value={r.caminho}>
          {r.titulo}
        </option>
      ))}
    </CampoSelecao>
  );
}
